@if (review) {
  <div class="adequacy-review-backdrop">
    <div
      class="adequacy-review"
      role="dialog"
      aria-modal="true"
      aria-labelledby="adequacy-review-title"
      cdkTrapFocus
      cdkTrapFocusAutoCapture
    >
      <button
        type="button"
        class="adequacy-review__close"
        aria-label="Fermer"
        [disabled]="submitting"
        (click)="close()"
      >
        <span aria-hidden="true">&times;</span>
      </button>

      <header class="adequacy-review__header">
        <h1 id="adequacy-review-title" tabindex="-1" cdkFocusInitial>Vos informations sont-elles toujours exactes&nbsp;?</h1>
        <p class="adequacy-review__intro">
          Nous vérifions périodiquement que les placements réalisés par notre
          intermédiaire restent adaptés à votre situation. Voici ce que nous
          détenons vous concernant&nbsp;:
        </p>
      </header>

      <div class="adequacy-review__body">
        @if (snapshot?.household; as household) {
          <section class="adequacy-review__block">
            <h2>Votre foyer</h2>
            <dl>
              @if (household.maritalSituation) {
                <div>
                  <dt>Situation familiale</dt>
                  <dd>{{ household.maritalSituation }}</dd>
                </div>
              }
              @if (household.childDependentCount !== null && household.childDependentCount !== undefined) {
                <div>
                  <dt>Enfants à charge</dt>
                  <dd>{{ household.childDependentCount }}</dd>
                </div>
              }
            </dl>

            @for (person of persons; track person.ownerId) {
              <div class="adequacy-review__person">
                <h3>
                  {{ labelFor(person) }}
                </h3>
                <dl>
                  @if (person.profession) {
                    <div>
                      <dt>Profession</dt>
                      <dd>{{ person.profession }}</dd>
                    </div>
                  }
                  @if (person.professionalSituation) {
                    <div>
                      <dt>Situation professionnelle</dt>
                      <dd>{{ person.professionalSituation }}</dd>
                    </div>
                  }
                  @if (person.totalIncome !== null && person.totalIncome !== undefined) {
                    <div>
                      <dt>Revenus annuels</dt>
                      <dd>{{ person.totalIncome | currency: 'EUR' : 'symbol' : '1.0-0' }}</dd>
                    </div>
                  }
                  @if (person.financialKnowledgeLabel) {
                    <div>
                      <dt>Connaissance et expérience financières</dt>
                      <dd>{{ person.financialKnowledgeLabel }}</dd>
                    </div>
                  }
                  @if (person.riskProfileLabel) {
                    <div>
                      <dt>Profil de risque</dt>
                      <dd>{{ person.riskProfileLabel }}</dd>
                    </div>
                  }
                  @if (person.riskToleranceLabel) {
                    <div>
                      <dt>Tolérance au risque</dt>
                      <dd>{{ person.riskToleranceLabel }}</dd>
                    </div>
                  }
                  @if (person.lossToleranceLabel) {
                    <div>
                      <dt>Capacité à subir des pertes</dt>
                      <dd>{{ person.lossToleranceLabel }}</dd>
                    </div>
                  }
                  @if (person.sustainabilityLabel) {
                    <div>
                      <dt>Préférences de durabilité</dt>
                      <dd>{{ person.sustainabilityLabel }}</dd>
                    </div>
                  }
                </dl>
              </div>
            }
          </section>

          <section class="adequacy-review__block">
            <h2>Votre situation patrimoniale</h2>
            <dl>
              @if (household.totalIncome !== null && household.totalIncome !== undefined) {
                <div>
                  <dt>Revenus du foyer</dt>
                  <dd>{{ household.totalIncome | currency: 'EUR' : 'symbol' : '1.0-0' }}</dd>
                </div>
              }
              @if (household.charges !== null && household.charges !== undefined) {
                <div>
                  <dt>Charges</dt>
                  <dd>{{ household.charges | currency: 'EUR' : 'symbol' : '1.0-0' }}</dd>
                </div>
              }
              @if (household.grossWorth !== null && household.grossWorth !== undefined) {
                <div>
                  <dt>Patrimoine brut</dt>
                  <dd>{{ household.grossWorth | currency: 'EUR' : 'symbol' : '1.0-0' }}</dd>
                </div>
              }
              @if (household.liabilities !== null && household.liabilities !== undefined) {
                <div>
                  <dt>Passifs</dt>
                  <dd>{{ household.liabilities | currency: 'EUR' : 'symbol' : '1.0-0' }}</dd>
                </div>
              }
              @if (household.netWorth !== null && household.netWorth !== undefined) {
                <div>
                  <dt>Patrimoine net</dt>
                  <dd>{{ household.netWorth | currency: 'EUR' : 'symbol' : '1.0-0' }}</dd>
                </div>
              }
              @if (household.availableCash !== null && household.availableCash !== undefined) {
                <div>
                  <dt>Disponibilités</dt>
                  <dd>{{ household.availableCash | currency: 'EUR' : 'symbol' : '1.0-0' }}</dd>
                </div>
              }
            </dl>
          </section>

          <section class="adequacy-review__block">
            <h2>Vos objectifs</h2>
            <dl>
              @if (household.objectives?.length) {
                <div>
                  <dt>Objectifs</dt>
                  <dd>{{ household.objectives.join(', ') }}</dd>
                </div>
              }
              @if (household.investmentHorizon) {
                <div>
                  <dt>Horizon de placement</dt>
                  <dd>{{ household.investmentHorizon }}</dd>
                </div>
              }
            </dl>
          </section>
        }

        @if (hasLegalEntities) {
          <section class="adequacy-review__block">
            <h2>Vos sociétés</h2>
            @for (entity of snapshot.legalEntities; track entity.ownerId) {
              <div class="adequacy-review__person">
                <h3>{{ entity.denomination }}</h3>
                <dl>
                  @if (entity.riskProfileLabel) {
                    <div>
                      <dt>Profil de risque</dt>
                      <dd>{{ entity.riskProfileLabel }}</dd>
                    </div>
                  }
                  @if (entity.sustainabilityLabel) {
                    <div>
                      <dt>Préférences de durabilité</dt>
                      <dd>{{ entity.sustainabilityLabel }}</dd>
                    </div>
                  }
                  @if (entity.holdings !== null && entity.holdings !== undefined) {
                    <div>
                      <dt>Encours</dt>
                      <dd>{{ entity.holdings | currency: 'EUR' : 'symbol' : '1.0-0' }}</dd>
                    </div>
                  }
                </dl>
              </div>
            }
          </section>
        }
      </div>

      @if (confirmingChange) {
        <p class="adequacy-review__confirmation">
          Vous allez mettre à jour votre situation avec votre conseiller. Ce choix ne pourra pas être
          annulé.
        </p>
        <footer class="adequacy-review__actions">
          <button
            type="button"
            class="adequacy-review__cta adequacy-review__cta--primary"
            [disabled]="submitting"
            (click)="declareChange()"
          >
            Confirmer
          </button>
          <button
            type="button"
            class="adequacy-review__cta adequacy-review__cta--secondary"
            [disabled]="submitting"
            (click)="cancelDeclareChange()"
          >
            Retour
          </button>
        </footer>
      } @else {
        <footer class="adequacy-review__actions">
          <button
            type="button"
            class="adequacy-review__cta adequacy-review__cta--primary"
            [disabled]="submitting"
            (click)="confirmUnchanged()"
          >
            Tout est exact, je confirme
          </button>
          <button
            type="button"
            class="adequacy-review__cta adequacy-review__cta--secondary"
            [disabled]="submitting"
            (click)="requestDeclareChange()"
          >
            Ma situation a évolué
          </button>
        </footer>
      }

      <p class="adequacy-review__notice">
        Si votre situation a évolué, nous vous guiderons pour mettre votre
        dossier à jour&nbsp;: cela prend environ 10 minutes.
      </p>
    </div>
  </div>
}
